import { Trans, t } from "@lingui/macro";
import { toBlob } from "html-to-image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BiCopy } from "react-icons/bi";
import { FiTwitter } from "react-icons/fi";
import { RiFileDownloadLine } from "react-icons/ri";

import { getServerUrl } from "config/backend";
import { HOME_URL } from "config/env";
import { useAffiliateCodes, useUserReferralCode } from "domain/referrals/hooks";
import { Token } from "domain/tokens";
import { helperToast } from "lib/helperToast";
import { getHomeUrl, getTwitterIntentURL } from "lib/legacy";
import { userAnalytics } from "lib/userAnalytics";
import { SharePositionActionEvent } from "lib/userAnalytics/types";

import Button from "components/Button/Button";

import { PositionShareCard } from "./PositionShareCard";
import Modal from "../Modal/Modal";

import "./PositionShare.css";

const IMAGE_MIME = "image/png";
const config = {
  quality: 1,
  canvasWidth: 576,
  canvasHeight: 324,
  type: IMAGE_MIME,
  cacheBust: true,
  pixelRatio: 2,
};

function waitForCardImages(element: HTMLElement) {
  const images = Array.from(element.querySelectorAll("img"));
  return Promise.all(
    images.map((image) => {
      if (image.complete) {
        return Promise.resolve();
      }

      return new Promise<void>((resolve) => {
        image.addEventListener("load", () => resolve(), { once: true });
        image.addEventListener("error", () => resolve(), { once: true });
      });
    })
  );
}

function getReferralLandingUrl(ref) {
  const fallbackHomeUrl = getHomeUrl();
  const rawHomeUrl = (HOME_URL || fallbackHomeUrl || "").trim();

  let resolvedUrl;
  try {
    resolvedUrl = new URL(rawHomeUrl);
  } catch (_error) {
    return "";
  }

  resolvedUrl.hash = "";
  resolvedUrl.pathname = "/referral";
  resolvedUrl.searchParams.delete("ref");
  if (ref?.code) {
    resolvedUrl.searchParams.set("ref", ref.code);
  }

  return resolvedUrl.toString();
}

function getSnapshotCardUrl(snapshotUrl: string) {
  return `${snapshotUrl.replace(/\/+$/, "")}/card.html`;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(new Error("Failed to read image blob as data URL"));
      }
    };
    reader.onerror = () => reject(new Error("Failed to read image blob as data URL"));
    reader.readAsDataURL(blob);
  });
}

async function copyBlobToClipboard(blob: Blob) {
  const clipboard = navigator.clipboard;
  const ClipboardItemCtor = (window as any).ClipboardItem;

  if (!clipboard || !ClipboardItemCtor) {
    return false;
  }

  await clipboard.write([
    new ClipboardItemCtor({
      [blob.type || IMAGE_MIME]: blob,
    }),
  ]);

  return true;
}

function downloadBlob(blob: Blob, filename: string) {
  const blobURL = window.URL.createObjectURL(blob);
  const tempLink = document.createElement("a");
  tempLink.style.display = "none";
  tempLink.href = blobURL;
  tempLink.setAttribute("download", filename);
  document.body.appendChild(tempLink);
  tempLink.click();
  document.body.removeChild(tempLink);
  setTimeout(() => window.URL.revokeObjectURL(blobURL), 100);
}

type Props = {
  entryPrice: bigint | undefined;
  indexToken: Token;
  isLong: boolean;
  leverage: bigint | undefined;
  markPrice: bigint;
  pnlAfterFeesUsd?: bigint;
  pnlAfterFeesPercentage: bigint;
  setIsPositionShareModalOpen: (isOpen: boolean) => void;
  isPositionShareModalOpen: boolean;
  account: string | undefined | null;
  chainId: number;
};

function PositionShare({
  entryPrice,
  indexToken,
  isLong,
  leverage,
  markPrice,
  pnlAfterFeesUsd,
  pnlAfterFeesPercentage,
  setIsPositionShareModalOpen,
  isPositionShareModalOpen,
  account,
  chainId,
}: Props) {
  const userAffiliateCode = useAffiliateCodes(chainId, account);
  const { userReferralCodeString } = useUserReferralCode(undefined, chainId, account);
  const effectiveReferralCode = userAffiliateCode.code || userReferralCodeString || null;
  const effectiveReferralData = useMemo(
    () => ({ code: effectiveReferralCode, success: Boolean(effectiveReferralCode) }),
    [effectiveReferralCode]
  );
  const [uploadedSnapshotUrl, setUploadedSnapshotUrl] = useState<string | null>(null);
  const [uploadedImageError, setUploadedImageError] = useState<string | null>(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [isUploadingSnapshot, setIsUploadingSnapshot] = useState(false);
  const [generatedImageBlob, setGeneratedImageBlob] = useState<Blob | null>(null);
  const [generatedImageDataUrl, setGeneratedImageDataUrl] = useState<string | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const referralLandingUrl = getReferralLandingUrl(effectiveReferralData);
  const buildTweetLines = useCallback(
    (snapshotCardUrl?: string) =>
      [
        `Check out my #${indexToken?.symbol} analysis on @NectarLiquid`,
        referralLandingUrl ? `Join via my referral: ${referralLandingUrl}` : "",
        snapshotCardUrl ? `Snapshot: ${snapshotCardUrl}` : "",
      ].filter(Boolean),
    [indexToken?.symbol, referralLandingUrl]
  );
  const tweetLines = useMemo(() => buildTweetLines(), [buildTweetLines]);
  const tweetText = useMemo(() => tweetLines.join("\n\n"), [tweetLines]);
  const tweetLink = useMemo(() => getTwitterIntentURL(tweetLines), [tweetLines]);

  const uploadSnapshot = useCallback(
    async (blob: Blob) => {
      const uploadUrl = getServerUrl(chainId, "/oracle-keeper/chart-snapshots");
      const formData = new FormData();
      formData.append("preparedImage", new File([blob], "position-share.png", { type: IMAGE_MIME }));

      const response = await fetch(uploadUrl, { method: "POST", body: formData });
      if (!response.ok) {
        throw new Error(`Snapshot upload failed with ${response.status}`);
      }

      const snapshotUrl = (await response.text()).trim();
      if (!snapshotUrl) {
        throw new Error("Snapshot upload returned an empty URL");
      }

      return snapshotUrl;
    },
    [chainId]
  );

  const generateCardImage = useCallback(async () => {
    const element = cardRef.current;
    if (!element) {
      throw new Error("Share card element is not available");
    }

    await waitForCardImages(element);

    const imageBlob = await toBlob(element, config);
    if (!imageBlob) {
      throw new Error("Failed to generate shareable image");
    }

    const imageDataUrl = await blobToDataUrl(imageBlob);
    return {
      blob: imageBlob,
      dataUrl: imageDataUrl,
    };
  }, []);

  useEffect(() => {
    if (!isPositionShareModalOpen) return;

    let isCancelled = false;

    (async function () {
      setIsGeneratingImage(true);
      setUploadedImageError(null);
      setUploadedSnapshotUrl(null);
      setGeneratedImageBlob(null);
      setGeneratedImageDataUrl(null);

      try {
        const generated = await generateCardImage();
        if (isCancelled) return;

        setGeneratedImageBlob(generated.blob);
        setGeneratedImageDataUrl(generated.dataUrl);
      } catch (_error) {
        if (!isCancelled) {
          setUploadedSnapshotUrl(null);
          setUploadedImageError(t`Failed to generate share image.`);
        }
      } finally {
        if (!isCancelled) {
          setIsGeneratingImage(false);
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [
    isPositionShareModalOpen,
    effectiveReferralCode,
    generateCardImage,
    entryPrice,
    indexToken?.symbol,
    isLong,
    leverage,
    markPrice,
    pnlAfterFeesUsd,
    pnlAfterFeesPercentage,
  ]);

  const ensureGeneratedImage = useCallback(async () => {
    if (generatedImageBlob && generatedImageDataUrl) {
      return {
        blob: generatedImageBlob,
        dataUrl: generatedImageDataUrl,
      };
    }

    const generated = await generateCardImage();
    setGeneratedImageBlob(generated.blob);
    setGeneratedImageDataUrl(generated.dataUrl);
    return generated;
  }, [generateCardImage, generatedImageBlob, generatedImageDataUrl]);

  const ensureSnapshotUrl = useCallback(async () => {
    if (uploadedSnapshotUrl) return uploadedSnapshotUrl;

    const { blob } = await ensureGeneratedImage();
    setIsUploadingSnapshot(true);
    try {
      const snapshotUrl = await uploadSnapshot(blob);
      setUploadedSnapshotUrl(snapshotUrl);
      return snapshotUrl;
    } finally {
      setIsUploadingSnapshot(false);
    }
  }, [uploadedSnapshotUrl, ensureGeneratedImage, uploadSnapshot]);

  async function handleDownload() {
    userAnalytics.pushEvent<SharePositionActionEvent>({
      event: "SharePositionAction",
      data: {
        action: "Download",
      },
    });

    try {
      const { blob } = await ensureGeneratedImage();
      downloadBlob(blob, "share.jpeg");
    } catch (_error) {
      helperToast.error(t`Failed to download share image.`);
    }
  }

  async function handleCopy() {
    userAnalytics.pushEvent<SharePositionActionEvent>({
      event: "SharePositionAction",
      data: {
        action: "Copy",
      },
    });

    try {
      const { blob, dataUrl } = await ensureGeneratedImage();
      if (await copyBlobToClipboard(blob)) {
        helperToast.success(t`Image copied to clipboard.`);
        return;
      }

      const clipboard = navigator.clipboard;
      if (clipboard && clipboard.writeText) {
        await clipboard.writeText(dataUrl);
        helperToast.success(t`Image data copied to clipboard.`);
        return;
      }

      helperToast.error(t`Clipboard image copy is not supported in this browser.`);
    } catch (_error) {
      helperToast.error(t`Failed to copy image.`);
    }
  }

  const handleTweet = useCallback(async () => {
    userAnalytics.pushEvent<SharePositionActionEvent>(
      {
        event: "SharePositionAction",
        data: {
          action: "ShareTwitter",
        },
      },
      { instantSend: true }
    );

    try {
      const { blob } = await ensureGeneratedImage();
      const nativeShare = navigator.share?.bind(navigator);
      const canShare = navigator.canShare?.bind(navigator);

      if (nativeShare && typeof File !== "undefined") {
        const file = new File([blob], "position-share.png", { type: IMAGE_MIME });
        const shareData: ShareData = {
          files: [file],
          text: tweetText,
          url: referralLandingUrl || undefined,
        };

        if (!canShare || canShare({ files: [file] })) {
          await nativeShare(shareData);
          return;
        }
      }
    } catch (_error) {
      // Fall through to web intent flow.
    }

    try {
      const snapshotUrl = await ensureSnapshotUrl();
      const composedTweetLink = getTwitterIntentURL(buildTweetLines(getSnapshotCardUrl(snapshotUrl)));
      try {
        const { blob } = await ensureGeneratedImage();
        if (await copyBlobToClipboard(blob)) {
          helperToast.success(t`Tweet opened. Paste to attach image (Ctrl/Cmd+V).`);
        }
      } catch (_error) {
        // Ignore clipboard errors for tweet flow.
      }
      window.open(composedTweetLink, "_blank", "noopener,noreferrer");
    } catch (_error) {
      window.open(tweetLink, "_blank", "noopener,noreferrer");
      try {
        const { blob } = await ensureGeneratedImage();
        if (await copyBlobToClipboard(blob)) {
          helperToast.success(t`Tweet opened. Paste to attach image (Ctrl/Cmd+V).`);
          return;
        }
      } catch (_fallbackError) {
        // Ignore clipboard fallback errors.
      }
      helperToast.error(t`Tweet image upload failed. Opened tweet without image.`);
    }
  }, [buildTweetLines, ensureGeneratedImage, ensureSnapshotUrl, referralLandingUrl, tweetLink, tweetText]);

  return (
    <Modal
      className="position-share-modal"
      isVisible={isPositionShareModalOpen}
      setIsVisible={setIsPositionShareModalOpen}
      label={t`Share Position`}
    >
      <PositionShareCard
        entryPrice={entryPrice}
        indexToken={indexToken}
        isLong={isLong}
        leverage={leverage}
        markPrice={markPrice}
        pnlAfterFeesUsd={pnlAfterFeesUsd}
        pnlAfterFeesPercentage={pnlAfterFeesPercentage}
        userAffiliateCode={effectiveReferralData}
        ref={cardRef}
        loading={isGeneratingImage}
      />
      {uploadedImageError && <span className="error">{uploadedImageError}</span>}

      <div className="actions">
        <Button
          variant="secondary"
          disabled={isGeneratingImage || isUploadingSnapshot}
          className="mr-15"
          onClick={handleCopy}
        >
          <BiCopy className="icon" />
          <Trans>Copy</Trans>
        </Button>
        <Button
          variant="secondary"
          disabled={isGeneratingImage || isUploadingSnapshot}
          className="mr-15"
          onClick={handleDownload}
        >
          <RiFileDownloadLine className="icon" />
          <Trans>Download</Trans>
        </Button>
        <Button
          variant="secondary"
          disabled={isGeneratingImage || isUploadingSnapshot}
          className="mr-15"
          onClick={handleTweet}
        >
          <FiTwitter className="icon" />
          <Trans>Tweet</Trans>
        </Button>
      </div>
    </Modal>
  );
}

export default PositionShare;
